import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Select, typography } from '@ui';
import { useCoaches, useUserCoaches } from '@queries';
import { UserCoach } from '@data';
import { useTranslate } from '@i18n';

import ShareConfirmationModal from '../ShareConfirmationModal/ShareConfirmationModal';

import * as css from './CoachSection.css';

export const CoachSection = () => {
    const { data: coaches } = useCoaches();
    const { data: userCoaches } = useUserCoaches();
    const { watch, setValue } = useFormContext();
    const t = useTranslate();

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [hasConfirmed, setHasConfirmed] = useState(false);
    const [pendingCoachIds, setPendingCoachIds] = useState<string[] | null>(null);

    const coachIds = watch('coachIds');

    const options = useMemo(() => {
        if (!coaches) return [];
        return coaches.map((coach) => ({ value: coach.userId, label: coach.name }));
    }, [coaches]);

    const onChange = useCallback(
        (values: string[]) => {
            const currentCoachIds = new Set((userCoaches ?? []).map((rel: UserCoach) => rel.coachId));
            const hasNew = values.some((id: string) => !currentCoachIds.has(id));
            if (hasNew && !hasConfirmed) {
                setPendingCoachIds(values);
                setConfirmOpen(true);
            } else {
                setValue('coachIds', values);
            }
        },
        [userCoaches, hasConfirmed, setValue],
    );

    return (
        <div className={css.coachSettings}>
            <p
                className={typography({
                    variant: 'h4',
                })}
            >
                {t('user_settings.coach_section')}
            </p>
            <div className={css.container}>
                <Select
                    options={options}
                    selectedValues={coachIds}
                    onChange={onChange}
                    placeholder={t('user_settings.coach_placeholder')}
                    label={t('user_settings.coach_label')}
                    width={200}
                    searchBox
                    multiple
                />
            </div>
            {confirmOpen && (
                <ShareConfirmationModal
                    open={confirmOpen}
                    onDismiss={() => {
                        setConfirmOpen(false);
                        setPendingCoachIds(null);
                    }}
                    onConfirm={() => {
                        if (pendingCoachIds) setValue('coachIds', pendingCoachIds);
                        setConfirmOpen(false);
                        setPendingCoachIds(null);
                        setHasConfirmed(true);
                    }}
                />
            )}
        </div>
    );
};
